import { useState, useEffect, useCallback } from 'react';
import { eventService } from '../services/eventService';
import { EventData, CalendarDot, GetEventsQuery } from '../types/event';

interface UseEventsState {
  events: EventData[];
  calendarDots: CalendarDot[];
  total: number;
  page: number;
  isLoading: boolean;
  error: string | null;
}

interface UseEventsReturn extends UseEventsState {
  loadMore: () => void;
  refetch: () => void;
  updateParticipation: (eventId: number, isParticipated: boolean) => void;
}

const LIMIT = 20;

export const useEvents = (query: GetEventsQuery): UseEventsReturn => {
  const [state, setState] = useState<UseEventsState>({
    events: [],
    calendarDots: [],
    total: 0,
    page: 1,
    isLoading: true,
    error: null,
  });

  const fetch = useCallback(async (page: number, append = false) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const data = await eventService.getEvents({ ...query, page, limit: LIMIT });
      setState((prev) => ({
        ...prev,
        events: append ? [...prev.events, ...data.events] : data.events,
        calendarDots: data.calendar_dots,
        total: data.pagination.total,
        page,
        isLoading: false,
      }));
    } catch {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: 'イベントの取得に失敗しました',
      }));
    }
  }, [JSON.stringify(query)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetch(1, false);
  }, [fetch]);

  const loadMore = useCallback(() => {
    fetch(state.page + 1, true);
  }, [fetch, state.page]);

  const refetch = useCallback(() => {
    fetch(1, false);
  }, [fetch]);

  const updateParticipation = useCallback((eventId: number, isParticipated: boolean) => {
    setState((prev) => ({
      ...prev,
      events: prev.events.map((e) =>
        e.id === eventId ? { ...e, is_participated: isParticipated } : e
      ),
    }));
  }, []);

  return { ...state, loadMore, refetch, updateParticipation };
};
